"use client";

import { motion } from "framer-motion";
import { 
  Search, 
  HelpCircle, 
  Book, 
  Settings, 
  Shield, 
  Smartphone, 
  LifeBuoy, 
  MessageCircle,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Mail
} from "lucide-react";
import Link from "next/link";
import { useState, useEffect } from "react";

const CATEGORIES = [
  { 
    id: "getting-started",
    title: "Getting Started", 
    desc: "Learn how to post requests and track pros.", 
    icon: <Sparkles className="text-amber-500" />, 
    color: "bg-amber-50" 
  },
  { 
    id: "payments",
    title: "Payments & Billings",
    desc: "Understanding quotes, invoices, and when you pay.",
    icon: <Book className="text-blue-500" />, 
    color: "bg-blue-50" 
  },
  {
    id: "safety",
    title: "Safety & Vetting",
    desc: "How contractor verification works on FixerLocal.",
    icon: <Shield className="text-emerald-500" />,
    color: "bg-emerald-50"
  },
  {
    id: "account",
    title: "Account Settings",
    desc: "Manage your profile and login details.",
    icon: <Settings className="text-slate-500" />,
    color: "bg-slate-50"
  },
  {
    id: "mobile",
    title: "Mobile & On the Go",
    desc: "Using FixerLocal from your phone's browser.",
    icon: <Smartphone className="text-indigo-500" />,
    color: "bg-indigo-50"
  },
  { 
    id: "enterprise",
    title: "Property Managers",
    desc: "Booking and tracking jobs across multiple properties.",
    icon: <LifeBuoy className="text-purple-500" />, 
    color: "bg-purple-50" 
  },
];

const ARTICLES: Record<string, { q: string, a: string }[]> = {
  "getting-started": [
    { q: "How do I create a new maintenance request?", a: "Click the 'Post New Request' button on your dashboard. Our AI will guide you through describing the issue, setting urgency, and estimating costs." },
    { q: "What happens after I post a request?", a: "FIXER immediately notifies the top-rated pros in your area. You'll receive a match notification usually within 15 minutes." }
  ],
  "payments": [
    { q: "When is my card charged?", a: "Your card isn't charged when you accept a bid or a contractor is assigned - there's no upfront payment or hold. You're only charged once the job is finished and you confirm you're happy with the work, at which point payment goes straight to the contractor." },
    { q: "Are quotes final?", a: "Quotes provided by the AI are estimates. Final pricing is confirmed with the pro on-site before work begins." }
  ],
  "safety": [
    { q: "How are pros verified?", a: "Contractors who register directly verify their business email via a one-time code. Some listings are sourced from public business directories and haven't been individually vetted until the contractor verifies their account - we recommend confirming licensing and insurance directly with your contractor before work begins." },
    { q: "Is my job protected if something goes wrong?", a: "FixerLocal isn't itself an insurer - contractors are independent operators responsible for their own work and any insurance they hold. If a job doesn't go as expected, contact our support team and we'll help mediate between you and the contractor." }
  ],
  "account": [
    { q: "How do I update my profile details?", a: "Go to your Dashboard and edit your name or phone number under Account Settings." },
    { q: "How do I reset my password?", a: "Use the 'Forgot Password' link on the login page to receive a reset code by email." },
    { q: "Can I have both a customer and contractor account?", a: "Customer and contractor accounts are separate - sign up for whichever matches how you're using FixerLocal. You can register a second account with a different email if you need both." }
  ],
  "mobile": [
    { q: "Is there a FixerLocal app?", a: "Not yet - FixerLocal works fully in your phone's web browser, so there's nothing to download. Add fixerlocal.com.au to your home screen for quick access." },
    { q: "Can I upload photos from my phone?", a: "Yes. When posting a job, you can attach photos or videos straight from your phone's camera or gallery." }
  ],
  "enterprise": [
    { q: "Can I manage multiple properties?", a: "There isn't a dedicated multi-property dashboard yet - for now, post and track a job for each property individually from your regular dashboard." },
    { q: "Do you offer special pricing for property managers?", a: "Not currently - every job uses the same bid-based pricing model. If you're managing jobs at scale, contact us and let us know what you need." }
  ]
};

const FAQS = [
  { q: "How do I confirm the contractor has finished?", a: "Once your contractor marks the job complete, you'll get a notification to review the work. Confirm it on your dashboard, then you'll be prompted to pay and can leave a review." },
  { q: "What happens if a pro doesn't show up?", a: "Message the contractor directly through in-app chat first. If you can't reach them, contact our support team with your job details - you're never charged for a job that wasn't completed." },
  { q: "Is my data shared with contractors?", a: "Only what's needed to do the job - your name, job description, and address - is shared with the specific contractor you select. See our Privacy Policy for full details." },
  { q: "Can I cancel a request after a match?", a: "You can cancel before accepting a bid at no cost. Once you've accepted a bid, discuss cancellation directly with your contractor." },
  { q: "How do I add a secondary property?", a: "Multi-property management isn't available yet - for now, post a separate job for each property's address." },
];

export default function HelpPage() {
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [openArticle, setOpenArticle] = useState<number | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Deep links like /help#safety (from the dashboard) or /help#account jump
  // straight into that category instead of the category grid.
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash && CATEGORIES.some((c) => c.id === hash)) {
      setSelectedCat(hash);
    }
  }, []);

  const activeArticles = selectedCat ? ARTICLES[selectedCat] || [
    { q: "How does this work?", a: "We're still writing up this section. Use the 'Email Support' option below and we'll help directly." },
  ] : [];

  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-6">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link href="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-slate-600 text-sm font-bold transition-colors mb-6 group">
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Return to Dashboard
            </Link>
            <h1 className="text-5xl font-black tracking-tight text-slate-950 mb-4">
              Help Center
            </h1>
            <p className="text-lg text-slate-500 font-medium max-w-xl leading-relaxed">
              Find answers, learn how FixerLocal works, or reach out to our support team.
            </p>
          </motion.div>

          {/* Search Box */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-full md:w-[400px]"
          >
            <div className="relative group">
              <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors">
                <Search size={22} />
              </div>
              <input 
                type="text" 
                placeholder="Search resources..."
                className="w-full h-16 rounded-[1.5rem] bg-white border border-slate-200 pl-14 pr-6 text-slate-900 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 transition-all font-medium shadow-xl shadow-slate-200/40"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 px-2 py-1 rounded-lg bg-slate-50 border border-slate-100 text-[10px] font-black text-slate-400">
                ⌘K
              </div>
            </div>
          </motion.div>
        </div>

        {/* Categories Grid or Article View */}
        <div className="mb-20">
          {!selectedCat ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {CATEGORIES.map((cat, i) => (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + (i * 0.05) }}
                  whileHover={{ y: -5 }}
                  onClick={() => setSelectedCat(cat.id)}
                  className="glass p-8 rounded-[2.5rem] border border-white/50 shadow-sm flex flex-col gap-6 cursor-pointer hover:border-white hover:shadow-xl transition-all group"
                >
                  <div className={`h-14 w-14 rounded-2xl ${cat.color} flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform`}>
                    <div className="flex items-center justify-center scale-125">
                      {cat.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-950 mb-2 group-hover:text-emerald-700 transition-colors">{cat.title}</h3>
                    <p className="text-sm text-slate-500 font-medium leading-relaxed">{cat.desc}</p>
                  </div>
                  <div className="mt-auto pt-4 flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest group-hover:text-emerald-600 transition-all">
                    Explore Library
                    <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass p-10 rounded-[3rem] border border-white"
            >
              <div className="flex items-center justify-between mb-10">
                <button 
                  onClick={() => { setSelectedCat(null); setOpenArticle(null); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-slate-50 text-slate-400 hover:text-slate-950 transition-all text-xs font-bold uppercase tracking-widest"
                >
                  <ArrowLeft size={16} />
                  Back to Categories
                </button>
                <div className="px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest border border-emerald-100 italic">
                  Knowledge Base: {CATEGORIES.find(c => c.id === selectedCat)?.title}
                </div>
              </div>

              <div className="space-y-4">
                {activeArticles.map((art, i) => (
                  <div key={i} className="rounded-2xl border border-slate-100 bg-white/50 overflow-hidden transition-all">
                    <button 
                      onClick={() => setOpenArticle(openArticle === i ? null : i)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-white transition-colors group"
                    >
                      <span className="font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">{art.q}</span>
                      <ChevronRight size={18} className={`text-slate-300 transition-transform duration-300 ${openArticle === i ? 'rotate-90 text-emerald-500' : ''}`} />
                    </button>
                    {openArticle === i && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        className="px-6 pb-6 text-sm text-slate-500 font-medium leading-relaxed italic border-t border-slate-50 pt-4"
                      >
                        {art.a}
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Quick FAQs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-black text-slate-950 mb-8 flex items-center gap-3">
              <HelpCircle className="text-emerald-500" />
              Common Questions
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <div key={i} className="rounded-2xl bg-white/50 border border-white hover:border-emerald-100 transition-all shadow-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="group w-full flex items-center justify-between p-5 text-left hover:bg-white transition-colors"
                  >
                    <span className="text-sm font-bold text-slate-700 group-hover:text-slate-950 transition-colors">{faq.q}</span>
                    <ChevronRight size={16} className={`text-slate-300 shrink-0 ml-4 transition-transform duration-300 ${openFaq === i ? 'rotate-90 text-emerald-500' : 'group-hover:text-emerald-500'}`} />
                  </button>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      className="px-5 pb-5 text-sm text-slate-500 font-medium leading-relaxed"
                    >
                      {faq.a}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="p-10 rounded-[3rem] bg-slate-950 text-white shadow-2xl shadow-slate-900/40 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:scale-110 transition-transform">
              <MessageCircle size={160} />
            </div>
            
            <h3 className="text-3xl font-black mb-4 relative z-10">Still need help?</h3>
            <p className="text-slate-400 font-medium mb-8 relative z-10 leading-relaxed max-w-sm">
              Send us a message and we'll help sort it out.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 relative z-10">
              <Link
                href="/contact"
                className="flex-1 h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-900/20 active:scale-95"
              >
                <Mail size={18} />
                Email Support
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
}
